import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { iv } from './invoiceTheme';
import { subscribeAmlAlerts, subscribeAmlSources, subscribeAmlSettings } from '../../utils/amlFirestoreService';
import {
  fetchAmlReview,
  runAmlBackfill,
  runAmlSyncNow,
  submitAmlReviewDecision,
  lookupAmlSubject
} from '../../utils/amlApi';
import { matchStatusLabel } from '../../utils/amlSchema';
import { InvoiceAmlRiskChip } from './InvoiceAmlRiskChip';
import { branchLabel } from '../../utils/invoiceSchema';
import { toast } from 'react-toastify';
import {
  RiskPager,
  riskTableCellSx,
  riskTableHeadCellSx,
  riskTableWrapSx,
  usePagedRows
} from './riskControlShared';

const formatMoney = (v) =>
  Number(v || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatTs = (value) => {
  if (!value) return '—';
  const d = typeof value?.toDate === 'function' ? value.toDate() : new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return `${d.toLocaleDateString('tr-TR')} ${d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}`;
};

const getActivePersonel = () => {
  try {
    return JSON.parse(localStorage.getItem('aktifPersonel') || 'null');
  } catch {
    return null;
  }
};

const statusChip = (status) => {
  if (status === 'strong') return <Chip size="small" color="error" label="Kritik" sx={{ fontWeight: 800, height: 22 }} />;
  if (status === 'possible')
    return <Chip size="small" color="warning" label="İnceleme" sx={{ fontWeight: 800, height: 22 }} />;
  return <Chip size="small" color="success" label="Temiz" sx={{ fontWeight: 800, height: 22 }} />;
};

const sourceStatusChip = (row) => {
  const st = String(row?.syncStatus || 'pending');
  if (st === 'ok' || st === 'unchanged') return <Chip size="small" color="success" label="Güncel" sx={{ height: 22 }} />;
  if (st === 'error') return <Chip size="small" color="warning" label="Hata" sx={{ height: 22 }} />;
  if (st === 'running') return <Chip size="small" label="Çalışıyor" sx={{ height: 22 }} />;
  return <Chip size="small" label="Bekliyor" sx={{ height: 22 }} />;
};

const SOURCE_TITLES = {
  masak: 'MASAK',
  un: 'BM',
  ofac: 'OFAC',
  eu: 'AB',
  uk: 'BK'
};

/**
 * MASAK bölümleri — üst sekme InvoiceIdentityRiskPanel’de.
 * section: 'alerts' | 'sources' | 'lookup'
 */
function InvoiceAmlPanel({
  sube,
  section = 'alerts',
  onKpis,
  onBusyChange,
  syncToken = 0,
  backfillToken = 0
}) {
  const personel = getActivePersonel();
  const canOperate = Boolean(personel?.id);
  const [alerts, setAlerts] = useState([]);
  const [sources, setSources] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [selected, setSelected] = useState(null);
  const [review, setReview] = useState(null);
  const [note, setNote] = useState('');
  const [lookupName, setLookupName] = useState('');
  const [lookupTax, setLookupTax] = useState('');
  const [lookupResult, setLookupResult] = useState(null);

  useEffect(() => {
    if (!sube) return undefined;
    setLoading(true);
    const u1 = subscribeAmlAlerts(
      sube,
      (rows) => {
        setAlerts(rows || []);
        setLoading(false);
      },
      () => setLoading(false)
    );
    const u2 = subscribeAmlSources((rows) => setSources(rows || []), () => {});
    const u3 = subscribeAmlSettings((data) => setSettings(data), () => {});
    return () => {
      u1?.();
      u2?.();
      u3?.();
    };
  }, [sube]);

  const runningSources = sources.filter((s) => s.syncStatus === 'running');
  const sourceErrors = sources.filter((s) => s.syncStatus === 'error');
  const syncedCount = sources.filter((s) => s.syncStatus === 'ok' || s.syncStatus === 'unchanged').length;

  const kpis = useMemo(() => {
    const todayKey = new Date().toLocaleDateString('tr-TR');
    const today = alerts.filter((a) => formatTs(a.createdAt).startsWith(todayKey));
    const possible = alerts.filter((a) => a.matchStatus === 'possible' && a.status !== 'resolved').length;
    const strong = alerts.filter((a) => a.matchStatus === 'strong' && a.status !== 'resolved').length;
    const lastSync =
      sources
        .map((s) => s.lastSuccessfulSyncAt)
        .filter(Boolean)
        .map(formatTs)
        .sort()
        .slice(-1)[0] || '—';
    return {
      today: today.length,
      possible,
      strong,
      lastSync,
      syncedCount,
      running: runningSources.length,
      errors: sourceErrors.length,
      mode: settings?.mode || 'monitor'
    };
  }, [alerts, sources, settings, runningSources.length, sourceErrors.length, syncedCount]);

  useEffect(() => {
    onKpis?.(kpis);
  }, [kpis, onKpis]);

  useEffect(() => {
    onBusyChange?.(busy);
  }, [busy, onBusyChange]);

  const handleSync = async () => {
    setBusy('sync');
    try {
      await runAmlSyncNow();
      toast.success('Liste güncellemesi başladı');
    } catch (err) {
      toast.error(err?.message || 'Senkron başarısız');
    } finally {
      setBusy('');
    }
  };

  const handleBackfill = async () => {
    setBusy('backfill');
    try {
      await runAmlBackfill(40);
      toast.success('Geçmiş tarama başladı');
    } catch (err) {
      toast.error(err?.message || 'Tarama başarısız');
    } finally {
      setBusy('');
    }
  };

  useEffect(() => {
    if (!syncToken) return;
    handleSync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncToken]);

  useEffect(() => {
    if (!backfillToken) return;
    handleBackfill();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backfillToken]);

  const openAlert = async (row) => {
    setSelected(row);
    setReview(null);
    setNote('');
    if (!canOperate) return;
    try {
      const data = await fetchAmlReview(sube, row.reviewId || row.id);
      setReview(data?.review || null);
    } catch (err) {
      toast.error(err?.message || 'Detay okunamadı');
    }
  };

  const runDecision = async (decision) => {
    if (!selected) return;
    setBusy(decision);
    try {
      await submitAmlReviewDecision({
        sube,
        reviewId: selected.reviewId || selected.id,
        decision,
        note
      });
      toast.success('İnceleme kaydedildi');
      setSelected(null);
    } catch (err) {
      toast.error(err?.message || 'Karar kaydedilemedi');
    } finally {
      setBusy('');
    }
  };

  const handleLookup = async () => {
    setBusy('lookup');
    try {
      const r = await lookupAmlSubject({ sube, name: lookupName, taxId: lookupTax });
      setLookupResult(r);
      toast.success(matchStatusLabel(r.status || 'clear'));
    } catch (err) {
      toast.error(err?.message || 'Kontrol edilemedi');
    } finally {
      setBusy('');
    }
  };

  const { page, setPage, slice, total, pageSize } = usePagedRows(alerts);

  const statusBanner =
    runningSources.length > 0 ? (
      <Alert severity="warning" sx={{ py: 0.2, flexShrink: 0, '& .MuiAlert-message': { fontSize: 12, fontWeight: 600 } }}>
        Listeler indiriliyor ({runningSources.length}). Birkaç dakika sürebilir.
      </Alert>
    ) : sourceErrors.length > 0 ? (
      <Alert severity="error" sx={{ py: 0.2, flexShrink: 0, '& .MuiAlert-message': { fontSize: 12, fontWeight: 600 } }}>
        {sourceErrors.length} kaynak güncellenemedi — Resmî listeler sekmesine bakın.
      </Alert>
    ) : null;

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 0.65, overflow: 'hidden' }}>
      {statusBanner}

      {section === 'alerts' ? (
        <Box sx={riskTableWrapSx}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ px: 1.25, py: 0.65, borderBottom: `1px solid ${iv.border}`, flexShrink: 0 }}
          >
            <Typography sx={{ fontSize: 13, fontWeight: 800, color: iv.text }}>MASAK uyarıları</Typography>
            <Typography sx={{ fontSize: 11.5, color: iv.textMuted, fontWeight: 600 }}>
              {kpis.mode === 'enforcement' ? 'Uygulama modu' : 'İzleme — fatura engellenmez'}
            </Typography>
          </Stack>
          {loading ? (
            <Box sx={{ flex: 1, display: 'grid', placeItems: 'center' }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <>
              <TableContainer sx={{ flex: 1, minHeight: 0 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {['Tarih', 'Müşteri', 'Banka', 'Tutar', 'Durum', 'Skor', 'Kaynak'].map((h) => (
                        <TableCell key={h} sx={riskTableHeadCellSx}>
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {slice.length ? (
                      slice.map((row) => (
                        <TableRow key={row.id} hover onClick={() => openAlert(row)} sx={{ cursor: 'pointer' }}>
                          <TableCell sx={riskTableCellSx}>{formatTs(row.createdAt)}</TableCell>
                          <TableCell sx={{ ...riskTableCellSx, fontWeight: 700 }}>
                            {row.subjectName || '—'}
                            <Typography sx={{ fontSize: 10.5, color: iv.textMuted, fontWeight: 600 }}>
                              {branchLabel(row.sube || sube)}
                            </Typography>
                          </TableCell>
                          <TableCell sx={riskTableCellSx}>{row.bank || '—'}</TableCell>
                          <TableCell sx={riskTableCellSx}>{formatMoney(row.amount)} ₺</TableCell>
                          <TableCell sx={riskTableCellSx}>{statusChip(row.matchStatus)}</TableCell>
                          <TableCell sx={riskTableCellSx}>{row.matchScore || 0}</TableCell>
                          <TableCell sx={riskTableCellSx}>{String(row.matchedSource || '').toUpperCase() || '—'}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={7} sx={{ ...riskTableCellSx, py: 3, textAlign: 'center', color: iv.textMuted }}>
                          Açık uyarı yok. Müşteri resmi listede çıkmazsa satır oluşmaz — bu normaldir.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <RiskPager page={page} pageSize={pageSize} total={total} onChange={setPage} />
            </>
          )}
        </Box>
      ) : null}

      {section === 'sources' ? (
        <Box sx={{ ...riskTableWrapSx, p: 1, overflow: 'auto' }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.75, px: 0.25 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 800 }}>Resmî yaptırım listeleri</Typography>
            <Button
              size="small"
              variant="contained"
              disabled={Boolean(busy)}
              onClick={handleSync}
              sx={{ textTransform: 'none', fontWeight: 800, fontSize: 12 }}
            >
              {busy === 'sync' ? 'Güncelleniyor…' : 'Şimdi güncelle'}
            </Button>
          </Stack>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(5, 1fr)' },
              gap: 0.75
            }}
          >
            {['masak', 'un', 'ofac', 'eu', 'uk'].map((id) => {
              const row = sources.find((s) => s.id === id || s.sourceId === id) || { sourceId: id };
              return (
                <Box
                  key={id}
                  sx={{
                    border: `1px solid ${iv.border}`,
                    borderRadius: 1.25,
                    p: 1,
                    bgcolor: '#fafbfc',
                    minHeight: 0
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={0.5}>
                    <Typography sx={{ fontWeight: 800, fontSize: 13 }}>{SOURCE_TITLES[id]}</Typography>
                    {sourceStatusChip(row)}
                  </Stack>
                  <Typography sx={{ fontSize: 11.5, color: iv.textMuted, mt: 0.75 }}>
                    Sync: {formatTs(row.lastSuccessfulSyncAt)}
                  </Typography>
                  <Typography sx={{ fontSize: 11.5, color: iv.textMuted }}>
                    {row.activeEntityCount || 0} kayıt
                  </Typography>
                  {row.lastError ? (
                    <Typography sx={{ fontSize: 11, color: iv.danger, mt: 0.4, lineHeight: 1.3 }}>
                      {row.lastError}
                    </Typography>
                  ) : null}
                </Box>
              );
            })}
          </Box>
          {syncedCount > 0 && !runningSources.length ? (
            <Typography sx={{ fontSize: 12, color: iv.textMuted, mt: 1, px: 0.25 }}>
              {syncedCount} liste yüklü. Uyarı tablosu yalnızca eşleşme olduğunda dolar.
            </Typography>
          ) : null}
        </Box>
      ) : null}

      {section === 'lookup' ? (
        <Box
          sx={{
            ...riskTableWrapSx,
            p: 2.25,
            maxWidth: 520,
            width: '100%',
            alignSelf: 'flex-start',
            overflow: 'auto'
          }}
        >
          <Typography sx={{ fontWeight: 800, fontSize: 15, mb: 0.4 }}>TCKN / VKN ile kontrol</Typography>
          <Typography sx={{ fontSize: 13, color: iv.textMuted, mb: 1.75 }}>
            Müşteri kartı veya fatura oluşturmadan resmi listelerde bakın. Sonuç Temiz / İnceleme / Kritik olarak
            görünür.
          </Typography>
          <Stack spacing={1.25}>
            <TextField
              size="small"
              label="TCKN veya VKN"
              value={lookupTax}
              onChange={(e) => setLookupTax(e.target.value)}
              fullWidth
            />
            <TextField
              size="small"
              label="Ad soyad / unvan (opsiyonel)"
              value={lookupName}
              onChange={(e) => setLookupName(e.target.value)}
              fullWidth
            />
            <Button
              variant="contained"
              disabled={Boolean(busy)}
              onClick={handleLookup}
              sx={{ textTransform: 'none', fontWeight: 800, alignSelf: 'flex-start', minHeight: 40, px: 2 }}
            >
              {busy === 'lookup' ? 'Kontrol ediliyor…' : 'Kontrol et'}
            </Button>
            {lookupResult ? (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ pt: 0.5 }} useFlexGap flexWrap="wrap">
                <InvoiceAmlRiskChip status={lookupResult.status} />
                {lookupResult.amlMatchScore ? (
                  <Typography sx={{ fontSize: 13, color: iv.textMuted }}>Skor {lookupResult.amlMatchScore}</Typography>
                ) : null}
                {lookupResult.sources?.length ? (
                  <Typography sx={{ fontSize: 13, color: iv.textMuted }}>
                    {lookupResult.sources.map((s) => String(s).toUpperCase()).join(', ')}
                  </Typography>
                ) : null}
              </Stack>
            ) : null}
          </Stack>
        </Box>
      ) : null}

      <Dialog open={Boolean(selected)} onClose={() => setSelected(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Uyarı detayı</DialogTitle>
        <DialogContent dividers>
          {selected ? (
            <Stack spacing={1.1}>
              <Typography sx={{ fontSize: 13 }}>Gönderen / müşteri: <b>{selected.subjectName}</b></Typography>
              <Typography sx={{ fontSize: 13 }}>IBAN: {selected.subjectIbanMasked || '—'}</Typography>
              <Typography sx={{ fontSize: 13 }}>
                Tutar: {formatMoney(selected.amount)} ₺ · Banka: {selected.bank || '—'}
              </Typography>
              <Typography sx={{ fontSize: 13 }}>Durum: {matchStatusLabel(selected.matchStatus)}</Typography>
              {canOperate && review ? (
                <>
                  <Typography sx={{ fontWeight: 800, mt: 0.5 }}>Eşleşen resmî kayıt</Typography>
                  {(review.matchedEntitySummaries || []).map((ent) => (
                    <Box key={ent.entityId} sx={{ p: 1, border: `1px solid ${iv.border}`, borderRadius: 1 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                        {String(ent.sourceId || '').toUpperCase()} · {ent.primaryName}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: iv.textMuted }}>
                        İsim {ent.breakdown?.nameScore || 0} · Alias {ent.breakdown?.aliasScore || 0} · Kimlik{' '}
                        {ent.breakdown?.idMatch ? 'var' : 'yok'} · Skor {ent.score}
                      </Typography>
                    </Box>
                  ))}
                  <TextField
                    label="Not"
                    fullWidth
                    size="small"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    sx={{ mt: 0.5 }}
                  />
                </>
              ) : (
                <Typography sx={{ fontSize: 13, color: iv.textMuted }}>
                  İnceleme detayı yükleniyor veya oturum gerekli.
                </Typography>
              )}
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelected(null)} sx={{ textTransform: 'none' }}>
            Kapat
          </Button>
          {canOperate ? (
            <>
              <Button disabled={Boolean(busy)} onClick={() => runDecision('reviewed')} sx={{ textTransform: 'none' }}>
                İnceledim
              </Button>
              <Button
                disabled={Boolean(busy)}
                onClick={() => runDecision('false_positive')}
                sx={{ textTransform: 'none' }}
              >
                Yanlış eşleşme
              </Button>
              <Button
                disabled={Boolean(busy)}
                color="error"
                onClick={() => runDecision('confirmed')}
                sx={{ textTransform: 'none' }}
              >
                Doğrulanmış
              </Button>
              <Button disabled={Boolean(busy)} onClick={() => runDecision('hold')} sx={{ textTransform: 'none' }}>
                Beklet
              </Button>
            </>
          ) : null}
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default InvoiceAmlPanel;
